/**
 * Source validation and processing utilities
 * Handles various source formats and ensures clickability
 */

// The backend's own answer-generation prompt lists retrieved context as
// "[N] From {filename}, Page {page}: ...", and the model has been observed
// copying that leading "From " straight into its own citation instead of
// just the filename ("Source: From Admission Portal.pdf" instead of
// "Source: Admission Portal.pdf"). That extra word then gets sent to
// /file/getfile as part of the filename, which can never resolve - shown
// here in the pill/panel too, not just the click failing. Stripped at
// every point a filename is extracted below so it's fixed for messages
// already saved with this in their text, not only newly generated ones.
export const normalizeFilename = (name) => {
  if (!name) return name;
  return name.replace(/^\s*from\s+/i, '').trim();
};

/**
 * Validates and normalizes source objects to ensure they have the required properties for clickability
 * @param {Array} sources - Array of source objects
 * @returns {Array} - Array of validated and normalized source objects
 */
export const validateAndNormalizeSources = (sources) => {
  if (!Array.isArray(sources)) {
    console.warn('Sources is not an array:', sources);
    return [];
  }

  return sources.map((source, index) => {
    // Handle different source object structures
    let normalizedSource = {};

    if (source.filename && source.page !== undefined) {
      // Standard format: {filename: string, page: number}
      // itemId (when present) disambiguates two active files that share this
      // exact filename — pulled out to a top-level field so callers (the
      // citation click handler, eventually /file/getfile) don't have to know
      // to dig into `original` for it.
      normalizedSource = {
        filename: normalizeFilename(source.filename),
        page: parseInt(source.page, 10),
        itemId: source.item_id || source.sharepoint_item_id || null,
        original: source
      };
    } else if (source.Dateiname && source.Seitenzahl !== undefined) {
      // Backend format: {Dateiname: string, Seitenzahl: number}
      normalizedSource = {
        filename: normalizeFilename(source.Dateiname),
        page: parseInt(source.Seitenzahl, 10),
        itemId: source.item_id || source.sharepoint_item_id || null,
        original: source
      };
    } else if (typeof source === 'string') {
      // String format - try to parse
      const parsed = parseSourceString(source);
      if (parsed) {
        normalizedSource = {
          filename: normalizeFilename(parsed.filename),
          page: parsed.page,
          original: source
        };
      } else {
        console.warn(`Could not parse source string: ${source}`);
        return null;
      }
    } else {
      console.warn(`Unknown source format at index ${index}:`, source);
      return null;
    }

    // Validate the normalized source
    if (!normalizedSource.filename || isNaN(normalizedSource.page) || normalizedSource.page < 1) {
      console.warn(`Invalid source at index ${index}:`, normalizedSource);
      return null;
    }

    return normalizedSource;
  }).filter(Boolean); // Remove null entries
};

/**
 * Parses a source string into filename and page number
 * @param {string} sourceString - Source string to parse
 * @returns {Object|null} - Parsed source object or null if parsing fails
 */
export const parseSourceString = (sourceString) => {
  if (!sourceString || typeof sourceString !== 'string') {
    return null;
  }

  // Pattern 1: "Source: filename.pdf (Page N)"
  let match = sourceString.match(/Source:\s*(.+?\.(?:pdf|png|jpe?g))\s*\(Page\s*(\d+)\)/i);
  if (match) {
    return {
      filename: normalizeFilename(match[1].trim()),
      page: parseInt(match[2], 10)
    };
  }

  // Pattern 2: "filename.pdf, Page N" — no "Source:" prefix to anchor on, so
  // exclude "(", ")" and newlines from the filename group (never legal in a
  // real filename) to stop ".+?" from running past an earlier citation.
  match = sourceString.match(/([^()\n]+?\.(?:pdf|png|jpe?g)),\s*Page\s*(\d+)/i);
  if (match) {
    return {
      filename: normalizeFilename(match[1].trim()),
      page: parseInt(match[2], 10)
    };
  }

  // Pattern 3: "filename.pdf (Page N)" — same unanchored risk as Pattern 2.
  match = sourceString.match(/([^()\n]+?\.(?:pdf|png|jpe?g))\s*\(Page\s*(\d+)\)/i);
  if (match) {
    return {
      filename: normalizeFilename(match[1].trim()),
      page: parseInt(match[2], 10)
    };
  }

  // Pattern 4: "Source: filename.pdf, Page N"
  match = sourceString.match(/Source:\s*(.+?\.(?:pdf|png|jpe?g)),\s*Page\s*(\d+)/i);
  if (match) {
    return {
      filename: normalizeFilename(match[1].trim()),
      page: parseInt(match[2], 10)
    };
  }

  // Pattern 5: "(filename.pdf, Page N)" with an optional stray "*" right
  // before the "(" and/or right after the ")" — e.g. "(file.pdf, Page 4)*"
  // or "*(file.pdf, Page 4)*". Unlike Pattern 3, the asterisk here sits
  // outside the parens (markdown italics/emphasis wrapping the whole
  // citation, or a leftover marker), not inside them.
  match = sourceString.match(/\*?\(([^()*\n]+?\.(?:pdf|png|jpe?g)),\s*Page\s*(\d+)\)\*?/i);
  if (match) {
    return {
      filename: normalizeFilename(match[1].trim()),
      page: parseInt(match[2], 10)
    };
  }

  return null;
};

/**
 * Extracts sources from text content using regex patterns
 * @param {string} content - Text content to extract sources from
 * @returns {Array} - Array of extracted source objects
 */
export const extractSourcesFromContent = (content) => {
  if (!content || typeof content !== 'string') {
    return [];
  }

  const sources = [];

  // Every filename group below uses a NON-GREEDY ".+?" (not "[^,]+") — a
  // filename can genuinely contain a comma itself (e.g. an auto-exported
  // image name like "ChatGPT Image 27. Juli 2026, 14_04_01.png"), which made
  // "[^,]+" stop matching at that internal comma and fail the whole pattern,
  // leaving the citation as unconverted plain text. Patterns 1-3 below are
  // anchored by a distinguishing prefix ("[N]", "Source:", "(*"), so ".+?"
  // only ever expands within one citation's own text. Pattern 4 has no such
  // prefix, so its filename group additionally excludes "(", ")" and
  // newlines (never legal in a real filename) to stop it from running past
  // an earlier citation entirely and merging two into one bogus match.

  // Pattern 1: [N] filename.pdf, Page N
  const detailedPattern = /\[(\d+)\]\s+(.+?\.(?:pdf|png|jpe?g)),\s*Page\s*(\d+)/gi;
  let match;
  while ((match = detailedPattern.exec(content)) !== null) {
    sources.push({
      filename: normalizeFilename(match[2].trim()),
      page: parseInt(match[3], 10),
      sourceType: 'detailed_ref'
    });
  }

  // Pattern 2: Source: filename.pdf, Page N
  const simplePattern = /Source:\s*(.+?\.(?:pdf|png|jpe?g)),\s*Page\s*(\d+)/gi;
  while ((match = simplePattern.exec(content)) !== null) {
    sources.push({
      filename: normalizeFilename(match[1].trim()),
      page: parseInt(match[2], 10),
      sourceType: 'simple_ref'
    });
  }

  // Pattern 3: (*filename.pdf, Page N*)
  const newFormatPattern = /\(\*(.+?\.(?:pdf|png|jpe?g)),\s*Page\s*(\d+)\*\)/gi;
  while ((match = newFormatPattern.exec(content)) !== null) {
    sources.push({
      filename: normalizeFilename(match[1].trim()),
      page: parseInt(match[2], 10),
      sourceType: 'new_format'
    });
  }

  // Pattern 4: filename.pdf (Page N) — no "[N]"/"Source:"/"(*" prefix to
  // anchor on, so exclude "(", ")" and newlines from the filename group to
  // stop ".+?" from running past an earlier citation already matched above.
  const altPattern = /([^()\n]+?\.(?:pdf|png|jpe?g))\s*\(Page\s*(\d+)\)/gi;
  while ((match = altPattern.exec(content)) !== null) {
    sources.push({
      filename: normalizeFilename(match[1].trim()),
      page: parseInt(match[2], 10),
      sourceType: 'alt_format'
    });
  }

  // Pattern 5: "(filename.pdf, Page N)" with an optional stray "*" right
  // before the "(" and/or right after the ")" — e.g. "(file.pdf, Page 4)*"
  // or "*(file.pdf, Page 4)*". Unlike Pattern 3, the asterisk sits outside
  // the parens (markdown emphasis wrapping the whole citation, or a
  // leftover marker), not immediately inside them.
  const outsideAsteriskPattern = /\*?\(([^()*\n]+?\.(?:pdf|png|jpe?g)),\s*Page\s*(\d+)\)\*?/gi;
  while ((match = outsideAsteriskPattern.exec(content)) !== null) {
    sources.push({
      filename: normalizeFilename(match[1].trim()),
      page: parseInt(match[2], 10),
      sourceType: 'outside_asterisk_format'
    });
  }

  return sources;
};

/**
 * Checks if a source object is clickable (has required properties)
 * @param {Object} source - Source object to check
 * @returns {boolean} - True if source is clickable
 */
export const isSourceClickable = (source) => {
  return source &&
         source.filename &&
         typeof source.filename === 'string' &&
         source.filename.trim().length > 0 &&
         source.page !== undefined &&
         !isNaN(source.page) &&
         source.page > 0;
};

/**
 * Creates a standardized source object for display
 * @param {Object} source - Source object to standardize
 * @returns {Object} - Standardized source object
 */
export const createStandardSource = (source) => {
  if (!source) return null;

  const standardSource = {
    filename: source.filename || source.Dateiname || '',
    page: source.page || source.Seitenzahl || 0,
    original: source
  };

  return isSourceClickable(standardSource) ? standardSource : null;
};

/**
 * Processes sources from multiple sources (message.sources, extracted from content, etc.)
 * @param {Object} message - Message object containing sources
 * @returns {Array} - Array of processed and validated sources
 */
export const processMessageSources = (message) => {
  const allSources = [];

  // 1. Get sources from message.sources array
  if (message.sources && Array.isArray(message.sources)) {
    const validatedSources = validateAndNormalizeSources(message.sources);
    allSources.push(...validatedSources);
  }

  // 2. Get sources from message.source array (alternative property)
  if (message.source && Array.isArray(message.source)) {
    const validatedSources = validateAndNormalizeSources(message.source);
    allSources.push(...validatedSources);
  }

  // 3. Extract sources from content if no sources found
  if (allSources.length === 0 && message.content) {
    const extractedSources = extractSourcesFromContent(message.content);
    const validatedSources = validateAndNormalizeSources(extractedSources);
    allSources.push(...validatedSources);
  }

  // Remove duplicates based on filename and page
  const uniqueSources = allSources.filter((source, index, self) =>
    index === self.findIndex(s =>
      s.filename === source.filename && s.page === source.page
    )
  );

  return uniqueSources;
};
